import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Box, CircularProgress, Container, Stack, Tab, Tabs, Typography } from '@mui/material';

import { LUX_LOYALTY_PROGRAM_CONFIG } from '@luxuryescapes/lib-lux-loyalty';

import SectionNavigation from '~/components/Common/Elements/SectionNavigation';
import SectionNavigationLayout from '~/components/Common/Elements/SectionNavigationLayout';
import { expandTabRoutes, preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';
import LoyaltyTierCoinStack from '~/components/Icons/LoyaltyTierCoinStack';
import InfiniteLogo from '~/components/LuxLoyalty/InfiniteLogo';
import LuxPlusLogo from '~/components/LuxLoyalty/LuxPlusLogo';
import SocieteProgramNameLogo from '~/components/LuxLoyalty/SocieteProgramNameLogo';
import { LuxPlusPageContainer as LuxPlusOrdersPage } from '~/components/Purchases/Home/LuxPlus';

import { ROLE_ADMIN_USER, ROLE_LUX_LOYALTY_ADMIN, ROLE_LUX_PLUS_ADMIN } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import canShowTab from '~/utils/canShowTab';

import ActionRewardPage from './ActionReward/ActionRewardPage';
import CampaignsPage from './Campaigns/CampaignsPage';
import LoyaltyCustomRulePage from './CustomRule/LoyaltyCustomRulePage';
import InfiniteConciergePage from './InfiniteConcierge/InfiniteConciergePage';
import LoyaltyManualAccrualsPage from './LoyaltyManualAccrualsPage';
import LuxPlusPayloadBuilderPage from './LuxPlus/LuxPlusPayloadBuilderPage';
import LuxPlusPurchaseRestrictionsPage from './LuxPlus/LuxPlusPurchaseRestrictionsPage';
import OffersBySavingsPage from './LuxPlus/OffersBySavingsPage';
import PartnersPage from './Partners/PartnersPage';
import StatsPage from './Stats/StatsPage';

const LOYALTY_ADMIN_ROLES = [ROLE_LUX_LOYALTY_ADMIN];
// Custom Rules is accessible to general admins as well as loyalty admins.
const CUSTOM_RULE_ROLES = [ROLE_ADMIN_USER, ROLE_LUX_LOYALTY_ADMIN];
// Matches the "Loyalty" nav link's own gate — the LuxPlus tabs manage purchase eligibility,
// so they're restricted at the route level too, not just hidden from users via nav visibility.
const LUX_PLUS_ROLES = [ROLE_ADMIN_USER, ROLE_LUX_PLUS_ADMIN];

type TabGroup = 'societe' | 'lux-plus';

function isTabGroup(value: string | undefined): value is TabGroup {
  return value === 'societe' || value === 'lux-plus';
}

// Slug to land on by default when no (or an inaccessible) section is requested, per group.
const GROUP_DEFAULT_SLUG: Record<TabGroup, string> = {
  societe: 'campaigns',
  'lux-plus': 'purchase-restrictions',
};

const GROUP_ORDER: Array<TabGroup> = ['societe', 'lux-plus'];
const GROUP_LABELS: Record<TabGroup, string> = {
  societe: 'Société',
  'lux-plus': 'LuxPlus+',
};

interface TabConfig {
  label: string;
  slug: string;
  component: React.ComponentType;
  group: TabGroup;
  requiredRoles?: Array<string>;
}

export const TabsMap: Record<string, TabConfig> = {
  campaigns: {
    label: 'Campaigns',
    slug: 'campaigns',
    component: CampaignsPage,
    group: 'societe',
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  stats: {
    label: 'Offers',
    slug: 'stats',
    component: StatsPage,
    group: 'societe',
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  infinite: {
    label: 'Infinite',
    slug: 'infinite',
    component: InfiniteConciergePage,
    group: 'societe',
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  'manual-accruals': {
    label: 'Manual Accruals',
    slug: 'manual-accruals',
    component: LoyaltyManualAccrualsPage,
    group: 'societe',
  },
  'custom-rule': {
    label: 'Custom Rules',
    slug: 'custom-rule',
    component: LoyaltyCustomRulePage,
    group: 'societe',
    requiredRoles: CUSTOM_RULE_ROLES,
  },
  partners: {
    label: 'Partners',
    slug: 'partners',
    component: PartnersPage,
    group: 'societe',
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  'action-reward': {
    label: 'Action Reward',
    slug: 'action-reward',
    component: ActionRewardPage,
    group: 'societe',
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  'purchase-restrictions': {
    label: 'Purchase Restrictions',
    slug: 'purchase-restrictions',
    component: LuxPlusPurchaseRestrictionsPage,
    group: 'lux-plus',
    requiredRoles: LUX_PLUS_ROLES,
  },
  'payload-builder': {
    label: 'Payload Builder',
    slug: 'payload-builder',
    component: LuxPlusPayloadBuilderPage,
    group: 'lux-plus',
    requiredRoles: LUX_PLUS_ROLES,
  },
  'offers-by-savings': {
    label: 'Offers by Savings',
    slug: 'offers-by-savings',
    component: OffersBySavingsPage,
    group: 'lux-plus',
    requiredRoles: LUX_PLUS_ROLES,
  },
  orders: {
    label: 'Orders',
    slug: 'orders',
    component: LuxPlusOrdersPage,
    group: 'lux-plus',
    requiredRoles: LUX_PLUS_ROLES,
  },
} as const;

type TabKey = keyof typeof TabsMap;

type TabBannerVariant = 'platinum' | 'infinite';

interface TabBannerConfig {
  variant: TabBannerVariant;
  description: string;
}

// Société tabs use tier branding; LuxPlus+ tabs use the member-pricing banner below.
const TAB_BANNER_CONFIG: Partial<Record<TabKey, TabBannerConfig>> = {
  campaigns: {
    variant: 'platinum',
    description: 'Set up and manage member promotions, bonus points, and campaign eligibility.',
  },
  stats: {
    variant: 'platinum',
    description:
      'Generate complimentary eSIMs, manage Pay for Points offer settings, and view Société rewards inventory reservations.',
  },
  infinite: {
    variant: 'infinite',
    description: 'Enrol customers into Infinite and manage their dedicated concierge representatives.',
  },
  'manual-accruals': {
    variant: 'platinum',
    description: 'Manually add or adjust points when a booking did not earn automatically.',
  },
  'custom-rule': {
    variant: 'platinum',
    description: 'Create one-off earn rules for specific orders or customer situations.',
  },
  partners: {
    variant: 'platinum',
    description:
      'Manage Société point partners and their cost-per-point rate cards, review point issuance by month, and generate the monthly partner invoice.',
  },
  'action-reward': {
    variant: 'platinum',
    description: 'Create and update action-reward configurations for member actions.',
  },
};

const LUX_PLUS_BANNER_DESCRIPTIONS: Partial<Record<TabKey, string>> = {
  'purchase-restrictions': 'Manage which hotel offers are restricted to LuxPlus+ members across regions and schedules.',
  'payload-builder':
    'Build JSON payloads for LuxPlus+ promo card and banner carousel Lab remote flags, then paste them into Lab.',
  'offers-by-savings':
    'Review hotel offers with LuxPlus+ member pricing, spot low savings, and flag offers that need pricing attention.',
  orders: 'Review LuxPlus+ orders and member purchase activity.',
};

const INFINITE_LOGO_HEIGHT = 20;
const INFINITE_LOGO_WIDTH = (248 / 38) * INFINITE_LOGO_HEIGHT;
const SOCIETE_LOGO_HEIGHT = 20;
const SOCIETE_LOGO_WIDTH = (62 / 14) * SOCIETE_LOGO_HEIGHT;
const LUX_PLUS_LOGO_HEIGHT = 20;
const LUX_PLUS_LOGO_WIDTH = (74 / 10) * LUX_PLUS_LOGO_HEIGHT;
const TAB_BANNER_COIN_SIZE = 24;

function LoyaltyTabBanner({ config }: { config: TabBannerConfig }) {
  const { variant, description } = config;
  const tier = variant === 'infinite' ? 'infinite' : 'platinum';

  return (
    <Box
      sx={(theme) => ({
        mt: 2,
        mb: 2,
        px: { xs: 2.5, md: 3 },
        py: 2,
        borderRadius: 2,
        color: theme.palette.loyaltyTiers[tier].contrastText,
        background: theme.palette.loyaltyTiers[tier].gradient,
        boxShadow: theme.shadows[2],
      })}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0, color: 'inherit' }}>
          {variant !== 'infinite' && <LoyaltyTierCoinStack size={TAB_BANNER_COIN_SIZE} />}
          <Box sx={{ lineHeight: 0, display: 'flex', alignItems: 'center' }}>
            {variant === 'infinite' ? (
              <InfiniteLogo width={INFINITE_LOGO_WIDTH} height={INFINITE_LOGO_HEIGHT} aria-hidden />
            ) : (
              <SocieteProgramNameLogo width={SOCIETE_LOGO_WIDTH} height={SOCIETE_LOGO_HEIGHT} aria-hidden />
            )}
          </Box>
        </Stack>
        <Typography
          variant="body1"
          sx={{
            color: 'inherit',
            lineHeight: 1.5,
            fontWeight: 400,
            whiteSpace: { xs: 'normal', md: 'nowrap' },
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {description}
        </Typography>
      </Stack>
    </Box>
  );
}

function LuxPlusTabBanner({ description }: { description: string }) {
  return (
    <Box
      sx={(theme) => ({
        mt: 2,
        mb: 2,
        px: { xs: 2.5, md: 3 },
        py: 2,
        borderRadius: 2,
        color: theme.palette.common.white,
        background: 'linear-gradient(90deg, #243aa7 0%, #3e73df 55%, #5797f2 100%)',
        boxShadow: theme.shadows[2],
      })}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
        <Box sx={{ lineHeight: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <LuxPlusLogo width={LUX_PLUS_LOGO_WIDTH} height={LUX_PLUS_LOGO_HEIGHT} aria-hidden />
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: 'inherit',
            lineHeight: 1.5,
            fontWeight: 400,
            whiteSpace: { xs: 'normal', md: 'nowrap' },
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {description}
        </Typography>
      </Stack>
    </Box>
  );
}

// expandTabRoutes only appends one segment after the base path, so each group is
// registered against its own base to produce the full /loyalty/<group>/<slug> path.
for (const group of GROUP_ORDER) {
  const tabsInGroup = Object.fromEntries(Object.entries(TabsMap).filter(([, config]) => config.group === group));
  preRegisterSpotlightRoutes(expandTabRoutes(`/loyalty/${group}`, 'Loyalty', tabsInGroup));
}

export default function LoyaltyPageContainer() {
  const { group: rawGroupParam, section: rawSection } = useParams<{ group?: string; section?: string }>();
  const navigate = useNavigate();
  const { user, isLoading } = useCurrentUser();

  // Client-side tab changes keep the previous scroll offset. Payload Builder is tall
  // enough that Offers by Savings / Orders then render off-screen until a refresh.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [rawGroupParam, rawSection]);

  const programName = LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName;

  // Tab visibility is role-based, so it depends on the current user. On a fresh
  // page load (e.g. refresh) the current user is synced into Redux by HomeRoute
  // via an effect that commits one render AFTER this component first mounts, so
  // `user` is briefly null here. If we computed visibility during that window,
  // every role-gated tab would look hidden and the redirect below would fire —
  // permanently bouncing the user off their tab (e.g. custom-rule ->
  // manual-accruals via <Navigate replace>). HomeRoute guarantees auth has
  // resolved before we render, so a null user here means "not synced yet": wait.
  if (isLoading || !user) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const visibleTabs = Object.keys(TabsMap).filter((tab) => {
    const { requiredRoles } = TabsMap[tab];
    if (!requiredRoles) {
      return true;
    }
    return canShowTab(user, requiredRoles);
  });

  const requestedGroup: TabGroup = isTabGroup(rawGroupParam) ? rawGroupParam : 'societe';
  const currentTabKey = rawSection
    ? visibleTabs.find((key) => TabsMap[key].group === requestedGroup && TabsMap[key].slug === rawSection)
    : undefined;

  // When the requested group/section doesn't resolve to a visible tab, redirect to
  // that group's default tab if the user can see it, otherwise to any tab available
  // to them. This keeps the landing tab role-aware so users never hit an empty page.
  if (!currentTabKey) {
    const tabsInRequestedGroup = visibleTabs.filter((key) => TabsMap[key].group === requestedGroup);
    const fallbackGroup =
      tabsInRequestedGroup.length > 0
        ? requestedGroup
        : GROUP_ORDER.find((group) => visibleTabs.some((key) => TabsMap[key].group === group));

    if (fallbackGroup) {
      const tabsInFallbackGroup = visibleTabs.filter((key) => TabsMap[key].group === fallbackGroup);
      const fallbackKey =
        tabsInFallbackGroup.find((key) => TabsMap[key].slug === GROUP_DEFAULT_SLUG[fallbackGroup]) ??
        tabsInFallbackGroup[0];
      return <Navigate to={`/loyalty/${fallbackGroup}/${TabsMap[fallbackKey].slug}`} replace />;
    }
  }

  const currentTab = currentTabKey ? TabsMap[currentTabKey] : undefined;
  const CurrentComponent = currentTab?.component;
  const currentGroup: TabGroup = currentTab?.group ?? requestedGroup;
  const visibleGroups = GROUP_ORDER.filter((group) => visibleTabs.some((key) => TabsMap[key].group === group));
  const groupTabs = visibleTabs.filter((key) => TabsMap[key].group === currentGroup);
  const bannerConfig = currentTabKey && TAB_BANNER_CONFIG[currentTabKey];
  const luxPlusBannerDescription = currentTabKey && LUX_PLUS_BANNER_DESCRIPTIONS[currentTabKey];

  const targetSlugForGroup = (group: TabGroup): string => {
    if (group === currentGroup && currentTab) {
      return currentTab.slug;
    }
    const tabsInGroup = visibleTabs.filter((key) => TabsMap[key].group === group);
    const fallbackKey = tabsInGroup.find((key) => TabsMap[key].slug === GROUP_DEFAULT_SLUG[group]) ?? tabsInGroup[0];
    return TabsMap[fallbackKey]?.slug ?? '';
  };

  return (
    <Container>
      <Helmet>
        <title>{programName}</title>
      </Helmet>

      <SectionNavigationLayout
        navigation={
          <SectionNavigation
            label="Loyalty sections"
            currentTab={currentGroup}
            groups={[
              {
                items: visibleGroups.map((group) => ({
                  id: group,
                  label: GROUP_LABELS[group],

                  href: `/loyalty/${group}/${targetSlugForGroup(group)}`,
                })),
              },
            ]}
          />
        }
      >
        {bannerConfig && <LoyaltyTabBanner config={bannerConfig} />}
        {luxPlusBannerDescription && <LuxPlusTabBanner description={luxPlusBannerDescription} />}

        <Box
          sx={(theme) => ({
            borderBottom: 1,
            borderColor: 'divider',
            mb: 1.5,
            mt: bannerConfig || luxPlusBannerDescription ? 0 : 2,
            position: 'relative',
            zIndex: theme.zIndex.appBar,
            bgcolor: 'background.default',
            '& .MuiTab-root': {
              minHeight: 48,
              fontWeight: 600,
              textTransform: 'none',
              color: 'text.secondary',
              transition: 'color 0.2s ease',
              '&:hover': { color: 'text.primary' },
            },
            '& .MuiTab-root.Mui-selected': {
              color: theme.palette.primary.main,
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          })}
        >
          <Tabs
            value={currentTab ? currentTabKey : false}
            variant="scrollable"
            scrollButtons="auto"
            onChange={(_event, tab: string) => {
              navigate(`/loyalty/${currentGroup}/${TabsMap[tab].slug}`);
            }}
          >
            {groupTabs.map((tab) => (
              <Tab
                key={tab}
                value={tab}
                label={TabsMap[tab].label}
                to={`/loyalty/${currentGroup}/${TabsMap[tab].slug}`}
                component={Link}
                sx={{ minWidth: 30, px: 1.5 }}
              />
            ))}
          </Tabs>
        </Box>

        <Box key={currentTabKey}>{CurrentComponent && <CurrentComponent />}</Box>
      </SectionNavigationLayout>
    </Container>
  );
}
